import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  createQuestion,
  ERROR_SAVE_DRAFT,
  FOLDER_ID_CUSTOM,
  QUESTION_TYPES,
  SURVEY_TYPES,
} from '@api/instant_survey';
import { maxQuestionError, minQuestions } from '@api/surveys';

import { GATES } from '@constants';
import {
  ActionArea,
  Announcement,
  Button,
  IconButton,
  isBetweenMinAndMax,
  OverlayTriggerTooltip,
  toast,
  Toolbar,
} from '@utilities';
import { ContractIcon, ExpandIcon, GPTOutlinedIcon, OverviewIcon } from '@utilities/icons';

import {
  getIsPreviewDisabled,
  PreviewButton,
  PreviewModal,
  useCanPreviewSurveys,
  useSurveyPreview,
} from '../../../../components/SurveyPreview';
import {
  calculateQuestionNumbers,
  findQuestionLogicQuestionById,
  getSequentialMonadicConceptValidationError,
  isValidQuestionLogicSource,
} from '../../utilities/helpers';
import { SurveyContext } from '../../utilities/context';
import SurveyToolbar from '../SurveyToolbar';

import AddQuestionIconButton from './components/AddQuestionIconButton';
import ModalAIGeneratedQuestionnaire from './components/ModalAIGeneratedQuestionnaire';
import ModalInstantSurveyQuestion from './components/ModalInstantSurveyQuestion';
import DownloadDOCXButton from './components/DownloadDOCXButton';
import ModalQuestionsLibrary from './components/ModalQuestionLibrary';
import ModalSurveyTemplate from './components/ModalSurveyTemplate';
import QuestionnaireList from './components/QuestionnaireList';
import { cleanupLogicAfterEdit, cleanupLogicAfterRemoval } from './utilities/helpers';

import styles from './_index.module.scss';

const Questionnaire = ({ onNext, onPrevious }) => {
  const {
    activeGroups,
    activeQuestions,
    currentQuestionCount,
    currentQuestionnairePrice,
    instantSurveyType,
    isReadOnly,
    isResearchObjectivesTooLong,
    maxQuestions,
    saveInstantSurvey,
    setShowAIGenerationModal,
    setShowSurveyTemplateModal,
    setSurveyGoals,
    showAIGenerationModal,
    showSurveyTemplateModal,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);

  const [generationError, setGenerationError] = useState(false);
  const [generationErrorMessage, setGenerationErrorMessage] = useState('');
  const [isDetailedQuestionView, setIsDetailedQuestionView] = useState(true);
  const [isDuplicatingQuestion, setIsDuplicatingQuestion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState([]);

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showCustomQuestionModal, setShowCustomQuestionModal] = useState(false);
  const [showQuestionsLibraryModal, setShowQuestionsLibraryModal] = useState(false);
  const remainingQuestionSlots = Math.max(0, maxQuestions - currentQuestionCount);
  const hasQuestionSlots = remainingQuestionSlots > 0;
  const MAX_NUM_QUESTIONS_MESSAGE =
    'You already have the maximum number of questions. Remove one to add more.';

  const clearGenerationError = () => {
    setGenerationError(false);
    setGenerationErrorMessage('');
  };

  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canUseAIGenerationQuestionnaire } = permissions;
  const { client } = useStatsigClient();
  const canSurveyTemplate = client.checkGate(GATES.SURVEY_TEMPLATES);

  const deletedQuestions = survey.questions.filter(({ _delete }) => _delete);
  const hasTurfQuestion = activeQuestions?.some(({ turf }) => Boolean(turf));
  const isTurfSurvey = instantSurveyType?.id === SURVEY_TYPES.TURF;
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const shouldShowActions = !isReadOnly && previewQuestions?.length === 0;
  const sequentialMonadicConceptError = activeQuestions
    .map(getSequentialMonadicConceptValidationError)
    .find(Boolean);
  const questionLimitExceededError =
    currentQuestionCount > maxQuestions
      ? `This survey uses ${currentQuestionCount} questions; the maximum allowed is ${maxQuestions}. Remove questions before continuing.`
      : null;
  const questionLimitReachedMessage =
    currentQuestionCount === maxQuestions
      ? `You have reached the maximum of ${maxQuestions} questions. Remove one before adding another question.`
      : null;
  const questionnaireValidationError = sequentialMonadicConceptError || questionLimitExceededError;
  const questionnaireAnnouncementMessage =
    questionnaireValidationError || questionLimitReachedMessage;

  const firstErrorRef = useRef(null);

  const questionNumbersByQuestions = useMemo(
    () => calculateQuestionNumbers(survey?.questions || []),
    [survey?.questions]
  );

  const pipingQuestionsMap = useMemo(() => {
    const map = {
      childrenByParent: {},
      parentsByChild: {},
    };

    const pipingPattern = /data-mention-id="answer from q(\d+)"/g;

    activeQuestions.forEach((question) => {
      const questText = question.richText || question.text || '';
      let match;

      while ((match = pipingPattern.exec(questText)) !== null) {
        const parentQuestionNumber = parseInt(match[1], 10);
        const parentQuestionId = Object.keys(questionNumbersByQuestions).find(
          (id) => questionNumbersByQuestions[id] === parentQuestionNumber
        );

        const parentQuestion = survey?.questions.find(
          (q) => q.id === parseInt(parentQuestionId, 10)
        );
        if (!parentQuestion) continue;

        const currentQuestionId = question.id;

        if (!map.childrenByParent[parentQuestionId]) {
          map.childrenByParent[parentQuestionId] = [];
        }
        map.childrenByParent[parentQuestionId].push({
          questionId: currentQuestionId,
          questionText: question.richText || question.text,
        });

        if (!map.parentsByChild[currentQuestionId]) {
          map.parentsByChild[currentQuestionId] = [];
        }
        map.parentsByChild[currentQuestionId].push({
          questionId: parentQuestionId,
          questionText: parentQuestion.richText || parentQuestion.text,
        });
      }
    });
    return map;
  }, [survey?.questions, questionNumbersByQuestions, activeQuestions]);

  useEffect(() => {
    if (!survey?.questions?.length) return;

    const hasConceptImage =
      Array.isArray(survey?.groups) && survey.groups.some((group) => group?.conceptImage != null);

    if (!hasConceptImage && survey.questions.length > 0) {
      const hasQuestionsWithConceptImage = survey.questions.some(
        (question) => question.showConceptImage === true && !question._delete
      );

      if (hasQuestionsWithConceptImage) {
        const updatedQuestions = survey.questions.map((question) => {
          if (question._delete) return question;
          return {
            ...question,
            showConceptImage: false,
          };
        });

        updateSurvey({ questions: updatedQuestions }, { markDirty: false });
      }
    }
  }, [survey?.groups, survey?.questions]);

  useEffect(() => {
    return () => {
      setSurveyGoals('');
    };
  }, [setSurveyGoals]);

  const [canPreviewSurvey] = useCanPreviewSurveys();
  const {
    handleClosePreviewModal,
    handlePreviewButtonClick,
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    previewSelection,
    resetPreviewQuestion,
    showPreviewModal,
  } = useSurveyPreview({
    questions: activeQuestions,
    quotaGroups: activeGroups,
  });

  const createAIQuestion = async (question) => {
    const { aiGenerated, answers, text, type } = question;

    const controller = new AbortController();
    try {
      let data;
      data = await createQuestion({
        question: {
          aiGenerated,
          answers,
          folder: FOLDER_ID_CUSTOM,
          text,
          type,
        },
        signal: controller.signal,
      });
      return data;
    } catch (error) {
      if (!controller.signal.aborted) {
        toast.error('Error creating question');
      }
    }
  };

  const handleAcceptPreview = async () => {
    const availableSlots = Math.max(0, maxQuestions - currentQuestionCount);
    if (availableSlots === 0) {
      toast(MAX_NUM_QUESTIONS_MESSAGE);
      setPreviewQuestions([]);
      return;
    }

    const questionsToCreate = previewQuestions.slice(0, availableSlots);
    if (previewQuestions.length > availableSlots) {
      toast(`Only ${availableSlots} question${availableSlots === 1 ? '' : 's'} can be added.`);
    }

    const createdQuestions = (
      await Promise.all(questionsToCreate.map((question) => createAIQuestion(question)))
    ).filter(Boolean);

    if (!createdQuestions.length) {
      toast.error('Unable to add questions. Please try again.');
      return;
    }

    const updatedQuestions = [...activeQuestions, ...createdQuestions, ...deletedQuestions];

    updateSurvey(
      {
        questions: updatedQuestions,
      },
      { markDirty: true }
    );
    clearGenerationError();
    setPreviewQuestions([]);
    setSurveyGoals('');
  };

  const handleAddAIQuestions = (questions = []) => {
    const availableSlots = Math.max(0, maxQuestions - currentQuestionCount);

    if (availableSlots === 0) {
      toast(MAX_NUM_QUESTIONS_MESSAGE);
      setPreviewQuestions([]);
      return;
    }

    if (!questions.length) {
      setPreviewQuestions([]);
      return;
    }

    if (questions.length > availableSlots) {
      toast(`Only ${availableSlots} question${availableSlots === 1 ? '' : 's'} can be generated.`);
    }

    setPreviewQuestions(questions.slice(0, availableSlots));
  };

  const handleCancelPreview = () => {
    clearGenerationError();
    setPreviewQuestions([]);
    setSurveyGoals('');
  };

  const handleAddLibraryQuestions = (questions) => {
    updateSurvey(
      {
        questions: [
          ...activeQuestions,
          ...questions,
          ...deletedQuestions.filter(
            // extra .filter checks for questions being added that were previously removed
            ({ id: existingQuestionId }) =>
              !questions.some(
                ({ id: selectedQuestionId }) => existingQuestionId === selectedQuestionId
              )
          ),
        ],
      },
      { markDirty: true }
    );
    clearGenerationError();
    handleQuestionLibraryModalClose();
  };

  const handleAddSurveyTemplate = (template) => {
    if (!Array.isArray(template?.questions) || template.questions.length === 0) {
      toast.error('Unable to apply template. Please try again.');
      return;
    }

    const existingQuestions = survey.questions
      // only mark questions for deletion that are not in the template (delete overrides the add)
      .filter(({ id }) => !template.questions.some((question) => question.id === id))
      .map((question) => ({ ...question, _delete: true }));
    const updatedQuestions = [...template.questions, ...existingQuestions];
    updateSurvey(
      {
        questionLogic: {},
        questions: updatedQuestions,
        surveyTemplateId: template.id,
      },
      { markDirty: true }
    );
    setPreviewQuestions([]);
    clearGenerationError();
    handleQuestionSurveyTemplateClose();
  };

  const handleAddMonadicSection = async () => {
    try {
      const data = await createQuestion({
        question: {
          answers: [],
          concepts: [],
          instantSurveyTypes: [SURVEY_TYPES.SEQUENTIAL_MONADIC],
          properties: {},
          subQuestions: [],
          text: 'Monadic Section',
          type: QUESTION_TYPES.SEQUENTIAL_MONADIC.value,
        },
      });

      handleQuestionCreate(data);
    } catch (error) {
      console.error('Error creating monadic section question block', error);
      toast.error('Error creating monadic section', { status: 'error' });
    }
  };

  const handleQuestionCreate = (question) => {
    updateSurvey(
      { questions: [...activeQuestions, question, ...deletedQuestions] },
      { markDirty: true }
    );
    clearGenerationError();
    handleQuestionModalClose();
  };

  const handleQuestionDuplicate = (question) => {
    setIsDuplicatingQuestion(true);
    setSelectedQuestion(question);
    setShowCustomQuestionModal(true);
  };

  const handleQuestionEdit = (question) => {
    setSelectedQuestion(question);
    setShowCustomQuestionModal(true);
  };

  const handleQuestionLibraryModalClose = () => {
    setShowQuestionsLibraryModal(false);
    setSelectedQuestion(null);
  };

  const handleQuestionSurveyTemplateClose = () => {
    setShowSurveyTemplateModal(false);
  };

  const handleQuestionModalClose = () => {
    setShowCustomQuestionModal(false);

    setIsDuplicatingQuestion(false);
    setSelectedQuestion(null);
  };

  const handleQuestionRemove = (question) => {
    const updatedQuestions = [...survey.questions];
    const questionIndex = updatedQuestions.findIndex(({ id }) => id === question.id);

    const [removedQuestion] = updatedQuestions.splice(questionIndex, 1);
    removedQuestion._delete = true;
    updateSurvey(
      {
        questionLogic: cleanupLogicAfterRemoval({
          question: removedQuestion,
          questionId: question.id,
          questionLogic: survey.questionLogic,
          questions: updatedQuestions,
        }),
        questions: [...updatedQuestions, removedQuestion],
        // INSRV-2109: remove surveyTemplateId if all questions are removed from the questionnaire
        surveyTemplateId:
          updatedQuestions.filter(({ _delete }) => !_delete).length > 0
            ? survey.surveyTemplateId
            : null,
      },
      { markDirty: true }
    );
  };

  const handleQuestionUpdate = (question) => {
    const questionIndex = survey.questions.findIndex(({ id }) => id === selectedQuestion?.id);
    const updatedQuestions = [...survey.questions];

    const [removedQuestion] = updatedQuestions.splice(questionIndex, 1, question);
    if (removedQuestion.id !== question.id) {
      // a recommended question became a custom question, remove the original question
      removedQuestion._delete = true;
      updatedQuestions.push(removedQuestion);
    }

    if (questionIndex >= 0) {
      updateSurvey(
        {
          questionLogic: cleanupLogicAfterEdit({
            originalQuestion: selectedQuestion,
            question,
            questionLogic: survey.questionLogic,
            questions: updatedQuestions,
          }),
          questions: [...updatedQuestions],
        },
        { markDirty: true }
      );
    }
    handleQuestionModalClose();
  };

  const canGenerateMoreQuestions = () => remainingQuestionSlots > 0;

  const openAIGenerationModal = ({ resetGoals = false } = {}) => {
    if (!canGenerateMoreQuestions()) {
      toast(MAX_NUM_QUESTIONS_MESSAGE);
      return;
    }
    if (resetGoals) {
      setSurveyGoals('');
    }
    setShowAIGenerationModal(true);
  };

  const onContinue = async () => {
    if (isReadOnly) {
      onNext();
      return;
    }
    if (isTurfSurvey && !hasTurfQuestion) {
      toast.error('Please add at least one turf question before continuing.');
      return;
    }
    if (questionnaireValidationError) {
      toast.error(questionnaireValidationError);
      return;
    }
    const isLogicValid = validateQuestionLogic();
    if (!isLogicValid) {
      if (firstErrorRef.current) {
        firstErrorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
      return;
    }

    setIsSaving(true);
    try {
      await saveInstantSurvey();
    } catch (error) {
      toast.error(ERROR_SAVE_DRAFT);
      setIsSaving(false);
      return;
    }

    onNext();
  };

  const validateQuestionLogic = () => {
    const modifiedQuestionLogic = { ...survey.questionLogic };
    let isValid = true;
    const isInvalidQuestionSource = ({ logicQuestion, sourceQuestion }) =>
      !sourceQuestion?.id ||
      !logicQuestion ||
      !isValidQuestionLogicSource({
        currentQuestion: logicQuestion,
        questions: activeQuestions,
        sourceQuestion,
      });

    for (const questionId in modifiedQuestionLogic) {
      const question = modifiedQuestionLogic[questionId];
      const logicQuestion = findQuestionLogicQuestionById(activeQuestions, questionId);

      if (question.skip) {
        for (const row of question.skip.logic) {
          if (row.hasOwnProperty('url_variable')) {
            const quotaGroupIds = survey.groups.map(({ id }) => id);
            if (!row.values || row.values.length === 0) {
              row.hasAnswerError = true;
              isValid = false;
            } else {
              let quotaGroupsValid = true;
              for (const groupID of row.values) {
                if (!quotaGroupIds.includes(Number(groupID))) {
                  quotaGroupsValid = false;
                }
              }
              if (!quotaGroupsValid) {
                row.hasAnswerError = true;
                isValid = false;
              } else {
                row.hasAnswerError = false;
              }
            }
          } else if (row.hasOwnProperty('comparison_value')) {
            if (isInvalidQuestionSource({ logicQuestion, sourceQuestion: row.question })) {
              row.hasQuestionError = true;
              row.isFirstQuestionError = isValid;
              isValid = false;
            } else {
              row.hasQuestionError = false;
            }

            if (
              // check for empty comparison_value
              row.comparison_value === undefined ||
              row.comparison_value === null ||
              row.comparison_value === '' ||
              // check for invalid numeric value
              (row.question?.type === QUESTION_TYPES.NUMERIC.value &&
                !isBetweenMinAndMax({
                  max: row.question.properties?.maxNumber,
                  min: row.question.properties?.minNumber,
                  value: row.comparison_value,
                }))
            ) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          } else {
            if (isInvalidQuestionSource({ logicQuestion, sourceQuestion: row.question })) {
              row.hasQuestionError = true;
              row.isFirstQuestionError = isValid;
              isValid = false;
            } else {
              row.hasQuestionError = false;
            }
            if ((row.answers || []).length === 0) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          }
        }
      }

      if (question.disqualify) {
        if (question.disqualify.answers.length === 0) {
          question.disqualify.isFirstAnswerError = isValid;
          question.disqualify.hasAnswerError = true;
          isValid = false;
        } else {
          question.disqualify.hasAnswerError = false;
        }
      }
    }

    updateSurvey({ questionLogic: modifiedQuestionLogic }, { markDirty: true });
    return isValid;
  };

  const isMinimumQuestionsThresholdUnmet = currentQuestionCount < minQuestions;
  const isContinueDisabled =
    isMinimumQuestionsThresholdUnmet ||
    isResearchObjectivesTooLong ||
    Boolean(questionnaireValidationError);

  return (
    <>
      <ActionArea
        buttons={[
          previewQuestions.length > 0
            ? [
                <Button
                  data-testid="cancel-button"
                  key="cancel-button"
                  onClick={handleCancelPreview}
                  text="Cancel"
                  variant="secondary"
                />,
                <Button
                  data-testid="insert-button"
                  key="insert-button"
                  onClick={handleAcceptPreview}
                  text="Insert Questions"
                  variant="ai"
                />,
              ]
            : [
                <Button
                  data-testid="previous-button"
                  key="previous-button"
                  onClick={onPrevious}
                  text="Previous"
                  variant="secondary"
                />,
                <Button
                  data-testid="review-button"
                  isDisabled={isContinueDisabled}
                  isLoading={isSaving}
                  key="review-button"
                  onClick={onContinue}
                  text="Go To Review"
                />,
              ],
        ]}
        className={styles['questionnaire']}
      >
        <SurveyToolbar isSaveAsDraftDisabled={isResearchObjectivesTooLong} />
        <div className={styles['questionnaire-pricing']}>
          <Toolbar
            tools={[
              ...(shouldShowActions
                ? [
                    <AddQuestionIconButton
                      key="add-question"
                      onAddQuestion={() => setShowQuestionsLibraryModal(true)}
                      onCreateQuestion={() => setShowCustomQuestionModal(true)}
                    />,
                    ...(!isVideoSurvey && canUseAIGenerationQuestionnaire
                      ? [
                          <div key="ai-generation">
                            <IconButton
                              isDisabled={!hasQuestionSlots}
                              onClick={() => {
                                if (!hasQuestionSlots) return;
                                openAIGenerationModal({ resetGoals: true });
                              }}
                              tooltip={
                                hasQuestionSlots ? 'AI Generated Questionnaire' : maxQuestionError
                              }
                            >
                              <GPTOutlinedIcon />
                            </IconButton>
                          </div>,
                        ]
                      : []),
                    ...(canSurveyTemplate
                      ? [
                          <IconButton
                            data-log="SurveyTemplates_Open"
                            data-log-properties={JSON.stringify({ type: instantSurveyType.id })}
                            key="survey-templates"
                            onClick={() => setShowSurveyTemplateModal(true)}
                            tooltip="Survey Templates"
                          >
                            <OverviewIcon />
                          </IconButton>,
                        ]
                      : []),
                    <OverlayTriggerTooltip
                      content={'Change to ' + (isDetailedQuestionView ? 'Overview' : 'Detailed')}
                      key="change-view"
                    >
                      <button
                        data-testid="change-view-button"
                        onClick={() => setIsDetailedQuestionView(!isDetailedQuestionView)}
                      >
                        {isDetailedQuestionView ? <ContractIcon /> : <ExpandIcon />}
                        {!canSurveyTemplate && <span>View</span>}
                      </button>
                    </OverlayTriggerTooltip>,
                    <DownloadDOCXButton key="download-docx" survey={survey} />,
                    ...(canPreviewSurvey
                      ? [
                          <PreviewButton
                            isDisabled={getIsPreviewDisabled(activeQuestions)}
                            key="preview-button"
                            location="questionnaire tab in verified voices"
                            onClick={() => handlePreviewButtonClick()}
                          />,
                        ]
                      : []),
                  ]
                : []),
            ]}
          >
            {!isVideoSurvey && <p>{currentQuestionnairePrice?.description}</p>}
          </Toolbar>
        </div>
        {questionnaireAnnouncementMessage && (
          <Announcement text={questionnaireAnnouncementMessage} variant="error" />
        )}
        <QuestionnaireList
          calculateQuestionNumbers={calculateQuestionNumbers}
          canPreviewSurvey={canPreviewSurvey}
          firstErrorRef={firstErrorRef}
          generationError={generationError}
          generationErrorMessage={generationErrorMessage}
          isDetailedQuestionView={isDetailedQuestionView}
          isVideoSurvey={isVideoSurvey}
          onAcceptPreview={handleAcceptPreview}
          onCancelPreview={handleCancelPreview}
          onCreateQuestion={() => setShowCustomQuestionModal(true)}
          onDuplicateQuestion={handleQuestionDuplicate}
          onEditQuestion={handleQuestionEdit}
          onOpenAIGenerator={() => openAIGenerationModal({ resetGoals: true })}
          onPreviewSurvey={handlePreviewButtonClick}
          onRemoveQuestion={handleQuestionRemove}
          onReprompt={() => {
            if (!canGenerateMoreQuestions()) {
              toast(MAX_NUM_QUESTIONS_MESSAGE);
              return;
            }
            clearGenerationError();
            openAIGenerationModal();
          }}
          pipingQuestionsMap={pipingQuestionsMap}
          questions={previewQuestions.length > 0 ? previewQuestions : activeQuestions}
          showActions={shouldShowActions}
          showPreviewBanner={previewQuestions.length > 0}
        />

        <div className={styles['questionnaire-list-actions']}>
          {!generationError && shouldShowActions && (
            <AddQuestionIconButton
              onAddMonadicSection={() => handleAddMonadicSection()}
              onAddQuestion={() => setShowQuestionsLibraryModal(true)}
              onCreateQuestion={() => setShowCustomQuestionModal(true)}
            />
          )}
        </div>
      </ActionArea>
      {showAIGenerationModal && (
        <ModalAIGeneratedQuestionnaire
          initialSurveyLength={Math.min(Math.max(remainingQuestionSlots, 0), 5)}
          maxQuestionsAvailable={remainingQuestionSlots}
          onAddAIQuestions={handleAddAIQuestions}
          onClose={() => setShowAIGenerationModal(false)}
          onGenerationError={(val, reason) => {
            if (val) {
              setGenerationError(true);
              setGenerationErrorMessage(reason || '');
              setPreviewQuestions([]);
            } else {
              clearGenerationError();
            }
          }}
        />
      )}
      {showCustomQuestionModal && (
        <ModalInstantSurveyQuestion
          isDuplicate={isDuplicatingQuestion}
          onClose={handleQuestionModalClose}
          onCreate={handleQuestionCreate}
          onUpdate={handleQuestionUpdate}
          originalQuestion={selectedQuestion}
          pipingQuestionsMap={pipingQuestionsMap}
        />
      )}
      {showQuestionsLibraryModal && (
        <ModalQuestionsLibrary
          maxQuestions={maxQuestions}
          onClose={handleQuestionLibraryModalClose}
          onSelectQuestions={handleAddLibraryQuestions}
          selectedQuestions={activeQuestions}
          surveyType={instantSurveyType}
        />
      )}
      {showSurveyTemplateModal && (
        <ModalSurveyTemplate
          hasWarning={activeQuestions.length > 0}
          onClose={handleQuestionSurveyTemplateClose}
          onSelectSurveyTemplate={handleAddSurveyTemplate}
          surveyType={instantSurveyType}
        />
      )}
      {showPreviewModal && (
        <PreviewModal
          instantSurveyId={survey.id}
          isActive={showPreviewModal}
          onClose={handleClosePreviewModal}
          onQuestionChange={handleUpdatePreviewQuestion}
          onQuotaGroupChange={handleUpdatePreviewQuotaGroup}
          onRestart={resetPreviewQuestion}
          previewSelection={previewSelection}
          questions={activeQuestions}
          quotaGroups={activeGroups}
        />
      )}
    </>
  );
};

Questionnaire.propTypes = {
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
};

export default Questionnaire;
